import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import Logo from '../../components/common/Logo';

interface OfferData {
  title: string;
  image: string;
  price: string;
  id?: number;
}

const OfferCompletion: React.FC = () => {
  const { offerCode } = useParams<{ offerCode: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [offerData, setOfferData] = useState<OfferData | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState<boolean>(false);
  const [trackingId, setTrackingId] = useState<string>('');
  const [email, setEmail] = useState<string>('');

  useEffect(() => {
    // Get data from location state
    if (location.state) {
      setPaymentSuccess(location.state.paymentSuccess || false);
      setTrackingId(location.state.trackingId || '');
      if (location.state.email) {
        setEmail(location.state.email);
      }
      if (location.state.offerData) {
        setOfferData(location.state.offerData);
      }
    }
  }, [location.state]);

  const trackingUrl = (email && trackingId) 
    ? `${window.location.origin}/offer/${btoa(`${email}|${trackingId}`)}`
    : '';

  const handleTrackOffer = () => {
    navigate(`/offer/${offerCode}/tracking`);
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleCopyTrackingId = async () => {
    try {
      await navigator.clipboard.writeText(trackingId);
      // Could add a toast notification here
    } catch (err) {
      console.error('Failed to copy tracking ID:', err);
    }
  };

  const handleShareTrackingId = async () => {
    const shareData = {
      title: 'My Offer Tracking ID',
      text: `Track my offer using this Tracking ID: ${trackingId}`,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      // Fallback: copy to clipboard
      handleCopyTrackingId();
    }
  };

  const handleCopyTrackingUrl = async () => {
    if (!trackingUrl) return;
    try {
      await navigator.clipboard.writeText(trackingUrl);
    } catch (err) {
      console.error('Failed to copy tracking URL:', err);
    }
  };

  const handleShareTrackingUrl = async () => {
    if (!trackingUrl) return;
    const shareData = {
      title: 'My Offer Tracking URL',
      text: `Track my offer using this link: ${trackingUrl}`,
      url: trackingUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      handleCopyTrackingUrl();
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Logo variant="full" size="small" />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {offerData && (
          <Box sx={{ mb: 4, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              {offerData.image && (
                <Card sx={{ width: 100, height: 100, flexShrink: 0 }}>
                  <CardMedia
                    component="img"
                    sx={{ height: 100, objectFit: 'cover' }}
                    image={offerData.image}
                    alt={offerData.title}
                  />
                </Card>
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {offerData.title}
                </Typography>
                <Typography variant="h5" color="primary" fontWeight="bold">
                  ${offerData.price}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          {paymentSuccess ? (
            <>
              <Box sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                backgroundColor: '#4caf50', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mx: 'auto',
                mb: 3
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="success.main" gutterBottom>
                Payment Successful!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Your payment has been processed successfully.
              </Typography>
            </>
          ) : (
            <>
              <Box sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                backgroundColor: '#f44336', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mx: 'auto',
                mb: 3
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="error.main" gutterBottom>
                Payment Failed
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Your payment could not be processed. Please try again.
              </Typography>
            </>
          )}
        </Box>

        {trackingId && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Tracking Information
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Your Tracking ID:
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                      {trackingId}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Copy Tracking ID">
                      <IconButton onClick={handleCopyTrackingId} size="small">
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Share Tracking ID">
                      <IconButton onClick={handleShareTrackingId} size="small">
                        <ShareIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Save this tracking ID to track your offer status. You can use it on the tracking page to monitor your purchase progress.
              </Typography>
            </Box>

            {trackingUrl && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ flex: 1, mr: 2, overflow: 'hidden' }}>
                      <Typography variant="body2" gutterBottom>
                        Your Tracking URL:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {trackingUrl}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Copy Tracking URL">
                        <IconButton onClick={handleCopyTrackingUrl} size="small">
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Share Tracking URL">
                        <IconButton onClick={handleShareTrackingUrl} size="small">
                          <ShareIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  You can also use this direct URL to check your order status anytime without manually entering your details.
                </Typography>
              </Box>
            )}
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {paymentSuccess && trackingId && (
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleTrackOffer}
            sx={{
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            Track Your Offer
          </Button>
        )}

        {!paymentSuccess && (
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={() => navigate(`/offer/${offerCode}/payment`)}
            sx={{
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            Try Payment Again
          </Button>
        )}

      
      </Paper>
    </Container>
  );
};

export default OfferCompletion;
